import { TrophyIcon } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import InfoButton from './components/InfoButton';
import PinEntryForm from './components/PinEntryForm';
import TapTourneyList from './components/TapTourneyList';
import TournamentHomeContent from './components/TournamentHomeContent';
import { CARD_STYLES } from './constants';

import { Button, Modal, Tabs } from '@/components';
import { Layout } from '@/components/GenericsTournaments';
import { DefaultTabsProps } from '@/components/Tabs';
import { TabType } from '@/components/Types';
import useAuth from '@/hooks/useAuth';
import { useCbuModal } from '@/hooks/useCbuModal';
import { useHandleChangesTabs } from '@/hooks/useHandleChangesTabs';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useMetaData } from '@/hooks/useMetaData';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import useTapTourney from '@/hooks/useTapTourney';
import useToast from '@/hooks/useToast';
import ROUTES from '@/router/routes';
import {
  getTdaxPrizesList,
  popupInscriptionData,
  TapRegistrationService,
} from '@/services/tda';
import { TAPorPremioTO, tourneyModalState } from '@/types/tda';
import {
  formatNumberToLocale,
  GENERICS_WORDS,
  isAndroid,
  TEXTS,
} from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

const HomeTDAxPrizes = () => {
  useScrollToTop();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { updateTapProperties } = useTapTourney();
  const { userInitialData: userLogged } = useAuth();
  const { isDesktop, isMobile } = useIsDesktop();
  const { handleTabChanges, tab } = useHandleChangesTabs();
  const [tourneyModal, setTourneyModal] = useState<tourneyModalState>({
    title: '',
    open: false,
    content: null,
    openSuccessModal: false,
  });
  const [successModal, setSuccessModal] = useState<tourneyModalState>({
    title: '',
    open: false,
    content: null,
    openSuccessModal: false,
  });
  const { ModalCBU, setModalCBU } = useCbuModal();
  const [showTourneyList, setShowTourneyList] = useState<boolean>(
    state?.showtourneys ?? false
  );

  useMetaData({
    title: 'Torneos de Amigos por Premios',
  });

  const toast = useToast();

  const tabs: DefaultTabsProps['tabs'] = [
    { name: GENERICS_WORDS.tdaClassic, disabled: false },
    {
      name: GENERICS_WORDS.tdaPremio,
      disabled: true,
    },
  ];

  const handleIsOpenInfo = (boolean: boolean) => {
    updateTapProperties({
      isOpenInfo: boolean,
    });
  };

  const { data: dataInit, refetch } = useQuery(
    'initDataTAP',
    async () => await getTdaxPrizesList(),
    {
      refetchOnWindowFocus: false,
      retry: false,
      onSuccess: (data) => {
        updateTapProperties({ llenoFormBancario: data.llenoFormBancario });
        if ('codError' in data && 'mensaje' in data) {
          toast.show(data.mensaje as string);
        }
      },
      onError: (error) => {
        const err = error as Error;
        toast.show(err.message);
      },
    }
  );

  useEffect(() => {
    handleTabChanges(1);
    if (dataInit && dataInit?.tengoPinDisponible) {
      // Verificar si el toast ya se mostró en esta sesión
      const toastShown = sessionStorage.getItem('pinDisponibleToastShown');

      if (!toastShown) {
        toast.show(
          'Ya conseguiste tu Pin Especial para participar en Torneo de Amigos por Premios. Ahora ingresalo seleccionando la opción participar'
        );
        // Guardar en sessionStorage que el toast ya se mostró
        sessionStorage.setItem('pinDisponibleToastShown', 'true');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataInit]);

  const onHandleTabsChanges = (tab: TabType) => {
    handleTabChanges(tab);
    navigate(ROUTES.friendsTournament);
  };

  const handleTourneyModal = (updates: Partial<tourneyModalState>) => {
    setTourneyModal((prev) => ({ ...prev, ...updates }));
  };

  const handleSuccessModal = (updates: Partial<tourneyModalState>) => {
    setSuccessModal((prev) => ({ ...prev, ...updates }));
  };

  const cardStyle = (index: number) => {
    switch (index) {
      case 0:
        return CARD_STYLES.tap1;
      case 1:
        return CARD_STYLES.tap2;
      case 2:
        return CARD_STYLES.tap3;
      default:
        return CARD_STYLES.tap1;
    }
  };

  const freeEntryContent = useCallback(
    (item: TAPorPremioTO, pinSugerido: string | undefined): JSX.Element => {
      const handleAccept = async () => {
        const res = await TapRegistrationService(item.id, '');
        if (res.ok) {
          handleTourneyModal({
            open: false,
            title: '',
            content: null,
            openSuccessModal: true,
          });
          handleSuccessModal({
            title: item.nombre,
            content: successContent(item),
          });
        } else {
          if (res.codError === 3303) {
            toast.show(
              `No existe cupo para el Torneo de Amigos por Premios seleccionado, en unos minutos podrás participar en otro Torneo de Amigos por ${formatNumberToLocale(
                item.premio ?? 0
              )}`
            );
            handleTourneyModal({
              open: false,
            });
          } else {
            if (res.codError === 3507) {
              toast.show(res.mensaje);
              handleTourneyModal({
                open: false,
              });
            } else {
              toast.show(
                res.codError === 3300
                  ? 'La inscripción libre ya fue utilizada en este torneo.'
                  : res.mensaje
              );
              handleSuccessModal({
                title: item.nombre,
                content: successContent(item),
              });
              handleTourneyModal({
                content: (
                  <PinEntryForm
                    handleTourneyModal={handleTourneyModal}
                    item={item}
                    pinSugerido={pinSugerido}
                  />
                ),
              });
            }
          }
        }
      };

      return (
        <div className="flex flex-col items-center content-center mt-4">
          <h1 className="font-bold text-center">
            Por ser el primer participante de este torneo, no necesitás ingresar
            un PIN Especial.
          </h1>
          <h1 className="font-bold text-center">Tu inscripción es libre.</h1>
          <span className=" text-center">
            El ganador del Torneo se lleva{' '}
            {`$${formatNumberToLocale(item.premio ?? 0)}`}
          </span>
          <div className="flex space-x-4 mt-4">
            <Button
              className="w-[8rem]"
              type="button"
              variant="secondary"
              onClick={() =>
                handleTourneyModal({
                  open: false,
                  title: '',
                  content: null,
                })
              }
            >
              Cancelar
            </Button>
            <Button
              className="w-[8rem]"
              type="button"
              variant="primary"
              onClick={handleAccept}
            >
              Aceptar
            </Button>
          </div>
        </div>
      );
    },
    [toast]
  );

  const showModalForeignerUser = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>) => {
      if (userLogged?.dtExtranjero) {
        toast.show(TEXTS.exclusiveForArgentinianDTs);
        event?.stopPropagation();
        event?.preventDefault();
        return true;
      } else {
        return false;
      }
    },
    [toast, userLogged?.dtExtranjero]
  );

  const onCardButtonClick = useCallback(
    async (item: TAPorPremioTO) => {
      if (!showModalForeignerUser()) {
        const inscriptionData = await popupInscriptionData(item.id);
        if (inscriptionData.pinObligatorio) {
          handleSuccessModal({
            title: item.nombre,
            content: successContent(item),
          });
          handleTourneyModal({
            content: (
              <PinEntryForm
                handleTourneyModal={handleTourneyModal}
                item={item}
                pinSugerido={inscriptionData.pinSugerido}
              />
            ),
            open: true,
            title: `Participá del ${item.nombre}`,
          });
        } else {
          handleTourneyModal({
            content: freeEntryContent(item, inscriptionData.pinSugerido),
            open: true,
            title: `Participá del ${item.nombre}`,
          });
        }
      }
    },
    [freeEntryContent, showModalForeignerUser]
  );

  const cardButton = useCallback(
    (item: TAPorPremioTO, index: number): JSX.Element => {
      // Check if user has already participated
      const hasParticipated = dataInit?.participaciones?.some(
        (participation) => participation.idTA === item.id
      );
      // Check if tournament is full
      const isTournamentFull = item.cantPart === 20;
      // Determine button state
      const isDisabled = hasParticipated || isTournamentFull;
      const buttonText = hasParticipated ? 'Participando' : 'Participar';

      const buttonClasses = `mx-auto px-8 border-none ${
        cardStyle(index).buttonColor
      } ${
        isDesktop
          ? ''
          : 'w-[101px] h-[36px] flex items-center justify-center ml-4 mt-2'
      }`;

      return (
        <Button
          className={buttonClasses}
          disabled={isDisabled}
          type="button"
          variant="primary"
          onClick={(event?: React.MouseEvent<HTMLButtonElement>) => {
            if (!showModalForeignerUser(event)) {
              onCardButtonClick(item);
            }
          }}
        >
          {buttonText}
        </Button>
      );
    },
    [
      dataInit?.participaciones,
      isDesktop,
      onCardButtonClick,
      showModalForeignerUser,
    ]
  );

  const successContent = (item: TAPorPremioTO) => {
    return (
      <div className="flex flex-col items-center content-center mt-4">
        <h1 className="font-bold uppercase text-center">
          {`YA ESTÁS PARTICIPANDO DEL ${item.nombre}.`}
        </h1>
        <p className="text-center font-semibold">{`Hacé tus cambios antes del inicio de la competencia en la ${item.nombreFecha} del torneo.`}</p>
        <span className="font-light text-center mt-4">
          Recordá que podés participar de más Torneos de Amigos por Premios y
          aumentar tus chances de ganar
        </span>
        {/* TODO: ACA SE VA A TENER QUE INTEGRAR LO QUE VIENE EN EL INIT */}
        <p className="uppercase text-center mt-4">
          {`LOS PRIMEROS TORNEOS ARRANCAN EN LA FECHA 5, HABRÁ OTRO GRUPO QUE INICIE EN LA FECHA 6 Y UN ÚLTIMO EN LA FECHA 7.`}
        </p>
      </div>
    );
  };

  const productCard = useCallback(
    (item: TAPorPremioTO, index: number): JSX.Element => {
      const hasParticipated = dataInit?.participaciones?.some(
        (participation) => participation.idTA === item.id
      );
      const isTournamentFull = item.cantPart === 20;
      const isDisabled = hasParticipated || isTournamentFull;

      return (
        <>
          {isDesktop ? (
            <div
              key={item.id}
              className="rounded-lg bg-white max-md:w-full mb-6 cursor-pointer hover:shadow-btn"
              onClick={!isDisabled ? () => onCardButtonClick(item) : undefined}
            >
              <div className="relative">
                <img
                  alt="torneo escuelas primarias"
                  className="max-md:hidden"
                  src={cardStyle(index).img}
                />
                <p className="absolute bottom-2 left-4 text-white py-2 font-bebas font-normal text-[22px] leading-6 max-md:hidden max-w-[140px]">
                  {item.nombre?.split('#')[0]}
                  <span className={cardStyle(index).hashColor}>
                    #{item.nombre?.split('#')[1]}
                  </span>
                </p>
              </div>
              <div
                className={`flex justify-between gap-3 px-4 py-2 rounded-t-lg md:rounded-none ${
                  cardStyle(index).subtitleBg
                } ${cardStyle(index).textAndCupColor}`}
              >
                <p className="text-[12px] font-semibold">{`CUPOS DISPONIBLES: ${
                  20 - (item.cantPart ?? 0)
                }`}</p>
                <p className="text-[12px] font-semibold">{`YA JUGANDO: ${item.cantPart}`}</p>
              </div>
              <div className="flex justify-center items-center gap-3 ">
                <div
                  className={`w-8 h-8 ${
                    cardStyle(index).cupBg
                  } flex justify-center items-center rounded-full mb-5`}
                >
                  <TrophyIcon
                    className={`${cardStyle(index).textAndCupColor}`}
                    size={20}
                  />
                </div>
                <div className="flex flex-col pt-6 gap-1">
                  <span className="font-bold text-black pb-1">
                    {`Participás por $${formatNumberToLocale(
                      item.premio ?? 0
                    )}`}
                  </span>
                  <span className="text-sm font-semibold">{`Inicio: ${item.nombreFecha}`}</span>
                  <span className="text-gdt-gray-3 text-sm font-semibold">{`Pin Especial: $${formatNumberToLocale(
                    item.tipoProducto.precioDeLista
                  )}`}</span>
                </div>
              </div>
              <div className="flex mt-5 mb-6">{cardButton(item, index)}</div>
            </div>
          ) : (
            <>
              <p className="font-bold text-[14px] mt-4">{item.nombre}</p>
              <div
                key={item.id}
                className="rounded-lg bg-white max-md:w-full mb-6 h-[159px]"
              >
                <div
                  className={`flex justify-between gap-3 px-4 py-2 rounded-t-lg ${
                    cardStyle(index).subtitleBg
                  } ${cardStyle(index).textAndCupColor}`}
                >
                  <p className="text-[12px] font-semibold">{`CUPOS DISPONIBLES: ${
                    20 - (item.cantPart ?? 0)
                  }`}</p>
                  <p className="text-[12px] font-semibold">{`YA JUGANDO: ${item.cantPart}`}</p>
                </div>
                <div className="flex flex-col items-start  ml-3 mb-6 pl-2 pt-4">
                  <div className="flex justify-center items-center gap-3 ">
                    <div
                      className={`w-8 h-8 ${
                        cardStyle(index).cupBg
                      } flex justify-center items-center rounded-full `}
                    >
                      <TrophyIcon
                        className={`${cardStyle(index).textAndCupColor}`}
                        size={20}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="font-bold text-black pb-1 text-[14px]">
                        {`Participás por $${formatNumberToLocale(
                          item.premio ?? 0
                        )}`}
                      </span>
                    </div>
                  </div>
                  <div className="flex">
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold">{`Inicio: ${item.nombreFecha}`}</span>
                      <span className="text-gdt-gray-3 text-sm font-semibold">{`Pin Especial: $${formatNumberToLocale(
                        item.tipoProducto.precioDeLista
                      )}`}</span>
                    </div>
                    {cardButton(item, index)}
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      );
    },
    [cardButton, dataInit?.participaciones, isDesktop, onCardButtonClick]
  );

  const actionFooter = () => {
    if (
      !showTourneyList &&
      dataInit?.abiertos &&
      dataInit.abiertos.length !== 0
    ) {
      if (
        dataInit &&
        dataInit?.participaciones &&
        dataInit?.participaciones.length > 0
      ) {
        return {
          buttonName: 'Ir a mis Torneos de Amigos por Premios',
          actionHandler: () => {
            setShowTourneyList(true);
          },
        };
      }
    } else return undefined;
  };

  const mobileLeftContent = () => {
    if (
      !isAndroid() &&
      isMobile &&
      showTourneyList &&
      dataInit?.abiertos?.length !== 0
    ) {
      return {
        backAction: () => {
          setShowTourneyList(false);
        },
      };
    }
    return {
      showMenuAction: true,
      items: true,
    };
  };

  useEffect(() => {
    if (
      isAndroid() &&
      !showTourneyList &&
      (dataInit?.participaciones?.length || 0) > 0
    ) {
      setShowTourneyList(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTourneyList, dataInit?.participaciones?.length]);

  useEffect(() => {
    if (
      showTourneyList ||
      (dataInit?.abiertos?.length === 0 &&
        dataInit?.participaciones?.length > 0)
    ) {
      if (!dataInit?.llenoFormBancario) {
        setModalCBU({
          visibility: true,
          title: 'Torneo de Amigos por Premios',
        });
      }

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTourneyList, dataInit?.abiertos?.length]);

  return (
    <Layout
      actionFooter={actionFooter()}
      adsSections={SECTION_NAME_FOR_ADS.torneodeamigospremios}
      backButton={
        isDesktop &&
        dataInit &&
        dataInit.participaciones.length > 0 &&
        showTourneyList
      }
      backButtonAction={() => setShowTourneyList(false)}
      className="px-4"
      main={() => {
        if (!showTourneyList && isAndroid()) {
          return (
            <div className="container max-w-5xl mx-auto px-5 my-20 text-base font-bold text-center">
              Para ingresar a esta sección es necesario hacerlo por navegador.
            </div>
          );
        }

        return (
          <div className="container max-w-5xl mx-auto px-5 ">
            {isDesktop && (
              <span className="text-2xl font-bold text-black">
                Torneos de Amigos por Premios
              </span>
            )}
            {(!showTourneyList ||
              (dataInit?.abiertos && dataInit.abiertos.length === 0)) && (
              <p className="font-semibold text-gdt-gray-2 my-4">
                Participá de torneos de 20 participantes. ¡El ganador de cada
                uno se lleva un increíble premio en efectivo!
              </p>
            )}
            {!showTourneyList &&
              dataInit?.abiertos &&
              dataInit.abiertos.length > 0 && (
                <>
                  <TournamentHomeContent
                    dataInit={dataInit}
                    isDesktop={isDesktop}
                    isMobile={isMobile}
                    productCard={productCard}
                    setShowTourneyList={setShowTourneyList}
                    showTourneyList={showTourneyList}
                  />
                </>
              )}
            {(showTourneyList || dataInit?.abiertos?.length === 0) && (
              <TapTourneyList tourneyList={dataInit?.participaciones} />
            )}
            <Modal
              controlledOnClose={() => {
                handleTourneyModal({
                  open: false,
                });
              }}
              disableBodyScroll={true}
              isControlledOpen={tourneyModal.open}
              render={() => tourneyModal.content}
              size="xs"
              title={tourneyModal.title}
              variant="controlled"
            />
            {/* segundo modal para cunado se concreto todo con exito */}
            <Modal
              controlledOnClose={() => {
                handleTourneyModal({
                  openSuccessModal: false,
                });
                refetch(); // Esto refrescará los datos
              }}
              disableBodyScroll={true}
              isControlledOpen={tourneyModal.openSuccessModal}
              render={() => successModal.content}
              size="xs"
              title={successModal.title}
              variant="controlled"
            />
            {/* modal de datos bancarios  */}
            {<ModalCBU />}
            <InfoButton handleIsOpenInfo={handleIsOpenInfo} hideButton={true} />
          </div>
        );
      }}
      mobile={{
        leftContent: mobileLeftContent(),
        centerContent: {
          title: 'Torneos de Amigos',
        },
      }}
      tabs={() => (
        <Tabs activeTab={tab} tabs={tabs} onClick={onHandleTabsChanges} />
      )}
      trackProps={{
        torneoAmigosPremios: Boolean(dataInit?.participaciones.length),
      }}
    />
  );
};

export default HomeTDAxPrizes;
